import React, { useState, useRef, useImperativeHandle } from 'react';
import { useIntl } from 'umi';
import { Button } from 'tea-component';
import useCos from '@gp/hooks/useCos';
import { loadVideoFromFile, loadImageFromFile } from '@gp/utils/common';
import { validateUploadFilesWithErrorNotice } from '@gp/utils/file';
import type { IMimeTypeGetter } from '@home/utils/file';

// const FILE_TYPE: {[keyName: string]: string} = {
//     video: '.avi,.wmv,.mpeg,.mp4,.m4v,.mov,.asf,.flv,.f4v,.rmvb,.rm,.3gp,.vob',
//     image: '.bmp,.jpg,.png,.tif,.gif,.pcx,.tga,.exif,.fpx,.svg,.psd,.cdr,.pcd,.dxf,.ufo,.eps,.ai,.raw,.WMF,.webp,.avif,.apng',
// }
export const video = '.mp4,.mov';
// export const video = '.avi,.wmv,.mpeg,.mp4,.m4v,.mov,.asf,.flv,.f4v,.rmvb,.rm,.3gp,.vob,.mkv';
export const image =
  '.bmp,.jpg,.png,.tif,.gif,.pcx,.tga,.exif,.fpx,.svg,.psd,.cdr,.pcd,.dxf,.ufo,.eps,.ai,.raw,.WMF,.webp,.avif,.apng';
export const FILE_TYPE: { [keyName: string]: string; } = {
  video,
  image,
};

/**
 * 根据扩展名获取文件类型
 * @param typeObj - 文件类型对象
 * @param extension - 扩展名
 * @returns 文件类型的编号，如果没有找到匹配的键，则返回0
 */
export function getFileTypeByKey(typeObj: typeof FILE_TYPE, extension: string): number | null {
  // 移除可能的点号前缀，并将扩展名转换为小写
  const ext = (extension.startsWith('.') ? extension : `.${extension}`).toLowerCase();
  const mapKey = {
    video: 2,
    image: 1,
  } as any;
  // eslint-disable-next-line no-restricted-syntax
  for (const key in typeObj) {
    const extensions = typeObj[key].split(',').map((ext) => ext.trim().toLowerCase()); // 分割扩展名列表并转换为小写
    if (extensions.includes(ext)) {
      // 检查扩展名是否在列表中（忽略大小写）
      return mapKey[key as string]; // 返回对应的键
    }
  }
  return 0; // 如果没有找到匹配的键，则返回null
}

export const getMimeType: IMimeTypeGetter = (file) => {
  if (!file) return '';
  const fileName = file?.name || '';
  const dotIndex = fileName?.lastIndexOf('.');
  if (dotIndex === undefined || dotIndex === -1) return '';
  return fileName?.substring(dotIndex) || '';
};

// 注意：使用多文件上传时，外部的回调函数不要副作用操作返回的结果数据，因为返回的数据没有深拷贝，这很可能影响页面中其它使用到的状态
function Upload(props: any, ref: any) {
  const {
    uploadFolderName = '', // 上传到腾讯云的文件夹名称
    qCouldService, // 腾讯云上传凭证信息接口
    qCouldServiceParams, // 腾讯云上传凭证信息接口对应的参数
    onStart,
    onSuccess,
    onError,
    onProgress,
    multiple = false,
    isSingleMultiple = false,
    getMimeType,
    onMultipleSizeOver, // 大小有误
    onMultipleFormatOver, // 格式有误
    onMultipleBeforeUploadFileMsg,
    onMultipleStart,
    onMultipleProgress,
    onMultipleTaskReady, // 获取上传任务编码
    onMultipleSuccess,
    onMultipleError, // 全部上传失败
    // onMultipleCompleted, // 上传结果，可能包含部分上传失败
    onMultipleSingleSuccess,
    onMultipleSingleError,
    accept,
    maxSize,
    maxWidth,
    minWidth,
    maxHeight,
    minHeight,
    onSizeOver,
    onWidthOrHeightOver, // 宽高超过最大限制
    onWidthOrHeightLess, // 宽高小于最小限制
    slot,
    type,
    limitedType: limitedTypeProps,
    id,
    isOpenDropUpload = false,
    isVisualFile= false,
    isToLowerCase = true,
  } = props;

  const { formatMessage } = useIntl();

  const limitedType = limitedTypeProps?.toLowerCase() || '';

  const { putObject, getObjectUrl } = useCos({
    qCouldService,
    qCouldServiceParams: qCouldServiceParams || uploadFolderName,
  });
  const acceptList = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const clickFile = () => {
    acceptList.current = limitedType || FILE_TYPE?.[type];
    const input = document.querySelector(`#file${id}`);
    // @ts-ignore
    input.click();
  };
  const uploadFile = async (val: any) => {
    if (typeof val?.persist === 'function') val?.persist();
    const file = val?.target?.files[0];
    let uploadFolderNameCur = '';

    const isValid = validateUploadFilesWithErrorNotice(val?.target?.files, formatMessage, {
      multiple,
      max: 999,
      accept,
      getMimeType,
      limitSize: maxSize,
    });
    if (!isValid) return;

    if (!file) return;
    const { size } = file;
    const fileType = file?.name?.split('.')?.pop();
    if (maxSize && size && size > maxSize) {
      onSizeOver?.();
      return;
    }
    if (type === 'image' || getFileTypeByKey(FILE_TYPE, fileType) === 1) {
      // 存在图片最小尺寸范围限制
      const hasMinLimit = !isNaN(minWidth) && !isNaN(minHeight);
      // 存在图片最大尺寸范围限制
      const hasMaxLimit = !isNaN(maxWidth) && !isNaN(maxHeight);
      if (hasMinLimit || hasMaxLimit) {
        const imageEl = await loadImageFromFile(file);
        if (hasMinLimit && (imageEl.width < minWidth || imageEl.height < minHeight)) {
          onWidthOrHeightLess?.();
          return;
        }
        if (hasMaxLimit && (imageEl.width > maxWidth || imageEl.height > maxHeight)) {
          onWidthOrHeightOver?.();
          return;
        }
      }
    }

    uploadFolderNameCur = uploadFolderName || type || fileType;
    // uploadFolderNameCur = isVisualFile ? (type || fileType) : (uploadFolderName || type || fileType);
    const path = `${uploadFolderNameCur}/${Date.now()}fileName=${file.name}`; // 这个path很重要，可以凭借它去腾讯云拿链接
    try {
      onStart?.(path);
      const res1 = await putObject({
        path,
        file,
        onProgress: (res: any) => onProgress(res, path),
      });
      // 清空标签原生value，防止重复上传同一个文件时，不触发change事件
      val.target.value = '';
      const res2 = await getObjectUrl({ path });
      let videoEl = null;
      if (type === 'video' || getFileTypeByKey(FILE_TYPE, fileType) === 2) {
        try {
          videoEl = await loadVideoFromFile(file);
        } catch (e) {
          console.error(e);
        }
      }
      onSuccess?.(res1, res2, path, videoEl);
    } catch (error) {
      onError?.(error);
    }
  };
  const multipleUpload = async (val: any) => {
    // path, file, onProgress
    if (typeof val?.persist === 'function') val?.persist();

    const isValid = validateUploadFilesWithErrorNotice(val?.target?.files, formatMessage, {
      multiple,
      max: 999,
      accept,
      getMimeType,
      limitSize: maxSize,
    });
    if (!isValid) return;

    const files = val?.target?.files;
    const list = [];
    const pathList: any[] = [];
    let uploadFolderNameCur = '';

    if (files?.length) {
      const sizeError = [];
      const formatError = [];
      const fileNames = [];
      const pathNames = [];
      const fileSizes = [];
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0;i < files.length;i++) {
        const item = files[i];
        const fileType = item?.name?.split('.')?.pop();
        // 判断每个文件是否超出上传大小限制
        const isMaxSize = maxSize && item?.size && item?.size > maxSize;
        // 判断每个文件是否格式不在规定范围内
        const dotIndex = item?.name?.lastIndexOf('.');
        let suffix = dotIndex > -1 ? item?.name?.slice(dotIndex) : ''; // 文件后缀,支持大小写
        if (isToLowerCase) {
          suffix = suffix?.toLowerCase();
        }
        const isInvalidFormat = suffix ? (limitedType || FILE_TYPE?.[type])?.indexOf(suffix) === -1 : true;
        sizeError.push(isMaxSize);
        formatError.push(isInvalidFormat);
        uploadFolderNameCur = uploadFolderName || type || fileType;
        // uploadFolderNameCur = isVisualFile ? (type || fileType) : (uploadFolderName || type || fileType);
        const path = `${uploadFolderNameCur}/${Date.now()}fileName=${item.name}`; // 这个path很重要，可以凭借它去腾讯云拿链接
        pathList.push({ path });
        fileNames.push(item?.name);
        pathNames.push(path);
        fileSizes.push(item?.size);
        // 超出最大限制及格式有误的文件不上传
        isMaxSize ||
          isInvalidFormat ||
          list.push({
            file: item,
            path,
            onProgress: (progressData: any) => {
              onMultipleProgress?.(path, progressData?.percent);
            },
            onTaskReady: (taskId: string) => {
              onMultipleTaskReady?.(path, taskId);
            },
          });
      }
      onMultipleBeforeUploadFileMsg?.(fileNames, pathNames, fileSizes);
      // 当有超过最大文件尺寸大小的文件时
      const flag = sizeError?.some((t: boolean) => t);
      const flag2 = formatError?.some((t: boolean) => t);
      const flag3 = sizeError?.every((t: boolean) => t);
      const flag4 = formatError?.every((t: boolean) => t);
      flag && onMultipleSizeOver?.(sizeError);
      flag2 && onMultipleFormatOver?.(formatError);
      if (flag3 || flag4) return;
      onMultipleStart?.(files, pathList);
      // 正常一个一个上传，这样才能单个获取到最新的上传结果
      const tempArr = pathList?.map(() => null);
      const resList1: any[] = [...tempArr];
      const resList2: any[] = [...tempArr];
      let isAllUploadFail = false;
      const reqList = []; /* , resObj: any = {} */
      for (let i = 0, len = list?.length;i < len;i++) {
        const t = list?.[i];
        reqList.push(
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          new Promise(async (resolve, reject) => {
            try {
              const res1 = await putObject(t);
              const res2 = await getObjectUrl(pathList?.[i]);
              resList1[i] = res1 || 'error';
              resList2[i] = res2 || 'error';
              if (!res1 || !res2) {
                onMultipleSingleError?.(pathList?.[i], i);
                reject(i);
              } else {
                onMultipleSingleSuccess?.(res1, res2, pathList?.[i], null, i);
                resolve(i);
              }
            } catch (error) {
              resList1[i] = 'error';
              resList2[i] = 'error';
              onMultipleSingleError?.(pathList?.[i], i);
              reject(i);
            }
          }),
        );
      }
      const res = await Promise.allSettled(reqList);
      if (res?.every((item) => item?.status === 'rejected')) isAllUploadFail = true;
      if (isAllUploadFail) {
        onMultipleError?.();
      } else {
        const res1 = resList1?.map((t) => {
          const isRejected = !t || t === 'error';
          return {
            status: isRejected ? 'rejected' : 'fulfilled',
            value: isRejected ? null : t,
          };
        });
        const res2 = resList2?.map((t) => {
          const isRejected = !t || t === 'error';
          return {
            status: isRejected ? 'rejected' : 'fulfilled',
            value: isRejected ? null : t,
          };
        });
        onMultipleSuccess?.(res1, res2, pathList);
      }
      val.target.value = '';
    }
  };
  const handleDrop = (event: any) => {
    if (typeof event?.persist === 'function') event?.persist();
    event.preventDefault();
    if (!isOpenDropUpload) return;
    setIsDragging(false);
    const { files } = event.dataTransfer;
    multipleUpload({
      target: {
        files,
      },
    });
  };

  useImperativeHandle(ref, () => ({ multipleUpload }));

  const handleDragOver = (event: any) => {
    event.preventDefault();
    if (!isOpenDropUpload) return;
    setIsDragging(true);
  };
  const handleOnDragLeave = (event: any) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const spanDom = (slot: any) => {
    if (isOpenDropUpload && isDragging) {
      return (
        <div className="dgb-visual-edit-upload-nodata dgb-visual-edit-upload-nodata--drapfile">
          <p>Release mouse</p>
        </div>
      );
    }
    return slot;
  };

  return (
    <>
      <input
        multiple={multiple}
        accept={limitedType || FILE_TYPE?.[type]}
        onChange={(val) => {
          // multiple ? multipleUpload(val) : uploadFile(val);
          multiple || isSingleMultiple ? multipleUpload(val) : uploadFile(val);
          // 避免上传中再次上传同一个文件，onChange不触发
          const input: any = document.querySelector(`#file${id}`);
          if (input?.value) {
            input.value = null;
          }
        }}
        style={{ display: 'none' }}
        id={`file${id}`}
        type="file"
      />
      <>
        {slot ? (
          <>
            <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleOnDragLeave}>
              <span onClick={clickFile}>{spanDom(slot)}</span>
            </div>
          </>
        ) : (
          <Button onClick={clickFile} style={{ marginLeft: 20 }}>
            Upload
          </Button>
        )}
      </>
    </>
  );
}

export default React.forwardRef(Upload);
